/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Search, Check} from 'lucide-react';
import { useAutoAnimate } from '@formkit/auto-animate/react';

export interface SelectOption {
    id: string;
    label: string;
    image?: string;
    [key: string]: any;
}

interface CustomSelectProps {
    label?: string;
    options: SelectOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    searchable?: boolean;
    renderOption?: (option: SelectOption, context: 'trigger' | 'list') => React.ReactNode;
    dropdownWidth?: string; // NEW: Allow wider dropdowns (e.g. 'w-[200px]')
    variant?: 'default' | 'minimal'; // NEW: For sidebar integration
}

const CustomSelect: React.FC<CustomSelectProps> = ({
    label,
    options,
    value,
    onChange,
    placeholder = "Select an option",
    disabled = false,
    searchable = true,
    renderOption,
    dropdownWidth = 'w-full', // Default to trigger width
    variant = 'default'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [direction, setDirection] = useState<'up' | 'down'>('down');
    const containerRef = useRef<HTMLDivElement>(null);
    const [parent] = useAutoAnimate();

    const selectedOption = options.find(o => o.id === value);

    // Smart Direction: Detect if we should open Up or Down
    useEffect(() => {
        if (isOpen && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            // High-End logic: If less than 400px below, open upward to stay within modal bounds
            setDirection(spaceBelow < 400 ? 'up' : 'down');
        }
    }, [isOpen]);

    // Close when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filtered options based on search
    const filteredOptions = useMemo(() => {
        if (!searchQuery) return options;
        return options.filter(o => 
            o.label.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [options, searchQuery]);

    const handleSelect = (id: string) => {
        onChange(id);
        setIsOpen(false);
        setSearchQuery('');
    };

    return (
        <div className={`flex flex-col gap-1 w-full ${isOpen ? 'relative z-[110]' : ''}`} ref={containerRef}>
            {label && <label className="block text-xs font-bold text-gray-500 uppercase">{label}</label>}
            
            <div className="relative" ref={parent}>
                {/* Trigger Button */}
                <button
                    type="button"
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    className={`
                        w-full flex items-center justify-between transition-all text-left outline-none
                        ${variant === 'minimal' 
                            ? 'p-0 bg-transparent border-none' 
                            : 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-2.5 text-sm'}
                        ${isOpen && variant !== 'minimal' ? 'ring-2 ring-brand-500 border-transparent' : ''}
                        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        ${variant !== 'minimal' ? 'hover:border-gray-300 dark:hover:border-gray-600' : ''}
                    `}
                >
                    <div className={`flex items-center gap-2 truncate ${variant === 'minimal' ? 'justify-center w-full' : ''}`}>
                        {selectedOption ? (
                            renderOption ? renderOption(selectedOption, 'trigger') : (
                                <>
                                    {selectedOption.image && (
                                        <img src={selectedOption.image} className="w-5 h-5 rounded-full object-cover" alt="" />
                                    )}
                                    <span className="truncate text-gray-900 dark:text-white font-medium">{selectedOption.label}</span>
                                </>
                            )
                        ) : (
                            <span className="text-gray-400">{placeholder}</span>
                        )}
                    </div>
                    {variant !== 'minimal' && <ChevronDown size={16} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
                </button>

                {/* Dropdown Menu */}
                {isOpen && (
                    <div className={`
                        absolute z-[120] ${dropdownWidth}
                        bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                        rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-60 
                        animate-in fade-in duration-200
                        ${direction === 'up' 
                            ? 'bottom-full mb-2 slide-in-from-bottom-2' 
                            : 'top-full mt-2 slide-in-from-top-2'}
                        ${dropdownWidth !== 'w-full' ? 'left-0' : ''}
                    `}>
                        
                        {/* Search Input */}
                        {searchable && options.length > 5 && (
                            <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                                <div className="relative">
                                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="search"
                                        autoFocus
                                        placeholder="Search..."
                                        autoComplete="off"
                                        autoCorrect="off"
                                        spellCheck={false}
                                        autoCapitalize="none"
                                        className="w-full bg-gray-50 dark:bg-gray-800/50 border-none rounded-lg pl-9 pr-3 py-1.5 text-sm outline-none dark:text-white focus:ring-1 ring-brand-500"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Options List */}
                        <div className="overflow-y-auto py-1">
                            {filteredOptions.length === 0 ? (
                                <div className="p-4 text-center text-xs text-gray-400 italic">No options found</div>
                            ) : (
                                filteredOptions.map((option) => {
                                    const isSelected = option.id === value;
                                    return (
                                        <button
                                            key={option.id}
                                            type="button"
                                            onClick={() => handleSelect(option.id)}
                                            className={`
                                                w-full flex items-center justify-between px-3 py-2.5 text-sm transition-colors
                                                ${isSelected 
                                                    ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-400' 
                                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'}
                                            `}
                                        >
                                            <div className="flex items-center gap-3 truncate">
                                                {renderOption ? renderOption(option, 'list') : (
                                                    <>
                                                        {option.image && (
                                                            <img src={option.image} className="w-5 h-5 rounded-full object-cover" alt="" />
                                                        )}
                                                        <span className={isSelected ? 'font-bold' : 'font-medium'}>{option.label}</span>
                                                    </>
                                                )}
                                            </div>
                                            {isSelected && (
                                                <div className="ml-2 text-brand-600 dark:text-brand-400 shrink-0">
                                                    <Check size={16} />
                                                </div>
                                            )}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomSelect;
